const normalize = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const matches = (field, names) =>
  [field.fieldName, field.fieldLabel].some((name) => names.includes(normalize(name)));
const read = (field, data) => data[field.fieldName]?.value ?? field.defaultValue ?? "";
const find = (fields, names) => fields.find((field) => matches(field, names));
const text = (value) => String(value ?? "");

const blockedUsers = new Set(
  "1,123,a,actuser,adm,admin,admin1,admin2,administrator,aspnet,backup,console,david,guest,john,owner,root,server,sql,support_388945a0,support,sys,test,test1,test2,test3,user,user1,user2,user3,user4,user5,video".split(",")
);
const blockedPasswords = new Set([
  "abc@123", "P@$$w0rd", "P@ssw0rd", "P@ssword123", "Pa$$word",
  "pass@word1", "Password!", "Password1", "Password22", "iloveyou!",
]);
const isIpv4 = (value) => {
  const parts = value.split(".");
  return parts.length === 4 && parts.every((part) =>
    /^(0|[1-9]\d{0,2})$/.test(part) && Number(part) <= 255
  );
};
const ipv4Number = (value) =>
  value.split(".").reduce((total, octet) => total * 256 + Number(octet), 0);
const parseCidr = (value) => {
  const parts = text(value).split("/");
  if (parts.length !== 2 || !isIpv4(parts[0]) || !/^\d{1,2}$/.test(parts[1]) || Number(parts[1]) > 32) return null;
  const size = 2 ** (32 - Number(parts[1]));
  const address = ipv4Number(parts[0]);
  return address % size === 0 ? { address, size } : null;
};
const isValidSshKey = (value) => {
  const [algorithm, encoded] = value.trim().split(/\s+/);
  if (!["ssh-rsa", "ssh-ed25519"].includes(algorithm) || !encoded ||
    !/^[a-z0-9+/]+={0,2}$/i.test(encoded) || encoded.length % 4 === 1) return false;
  let decoded;
  try {
    decoded = atob(encoded);
  } catch (error) {
    if (error.name === "InvalidCharacterError") return false;
    throw error;
  }
  let offset = 0;
  const readPart = () => {
    if (offset + 4 > decoded.length) return null;
    let length = 0;
    for (let i = 0; i < 4; i++) length = length * 256 + decoded.charCodeAt(offset++);
    if (offset + length > decoded.length) return null;
    const part = decoded.slice(offset, offset + length);
    offset += length;
    return part;
  };
  if (readPart() !== algorithm) return false;
  const first = readPart();
  if (!first) return false;
  if (algorithm === "ssh-ed25519") return first.length === 32 && offset === decoded.length;
  const modulus = readPart();
  if (!modulus || offset !== decoded.length) return false;
  const unsigned = modulus.replace(/^\0+/, "");
  const bits = unsigned.length
    ? (unsigned.length - 1) * 8 + Math.floor(Math.log2(unsigned.charCodeAt(0))) + 1
    : 0;
  return bits >= 2048;
};

const getOs = (fields, data) => {
  const field = find(fields, ["ostype", "operatingsystem", "platformdetails"]) ??
    find(fields, ["image", "osimage"]);
  const state = field && data[field.fieldName];
  const value = `${state?.label ?? ""} ${field ? read(field, data) : ""}`.toLowerCase();
  if (/windows/.test(value)) return "windows";
  if (/linux|ubuntu|debian|rhel|red hat|centos|suse|rocky|alma/.test(value)) return "linux";
  return "";
};

export const getAzureVmFieldError = (field, data, fields) => {
  const value = read(field, data);
  const label = field.fieldLabel || field.fieldName;
  const empty = Array.isArray(value) ? value.length === 0 : text(value).trim() === "";
  const unchecked = field.fieldType === "checkbox" &&
    ![true, 1, "true"].includes(typeof value === "string" ? value.toLowerCase() : value);
  if (field.isRequired && (empty || unchecked)) return `${label} is required.`;
  if (empty) return "";

  const str = text(value);
  const os = getOs(fields, data);
  const isText = ["text", "password", "number", "textarea"].includes(field.fieldType);
  if (!isText) return "";

  if (matches(field, ["subscription", "subscriptionid"])) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      ? "" : "Enter a valid Azure subscription ID (GUID).";
  }
  if (matches(field, ["resourcegroup", "resourcegroupname"])) {
    return str.length <= 90 && /^[\p{L}\p{N}_.()-]+$/u.test(str) && !str.endsWith(".")
      ? "" : "Use 1-90 letters, numbers, _, -, ., ( or ); do not end with a period.";
  }
  if (matches(field, ["virtualmachinename", "vmname", "computername", "hostname"])) {
    const separateHostname = find(fields, ["computername", "hostname"]);
    const isHost = matches(field, ["computername", "hostname"]) || !separateHostname;
    const max = isHost && os === "windows" ? 15 : 64;
    if (str.length > max) return `Use at most ${max} characters${os === "windows" && isHost ? " for a Windows host name" : ""}.`;
    const validName = isHost
      ? /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/i
      : /^[a-z0-9](?:[a-z0-9_.-]*[a-z0-9_])?$/i;
    if (!validName.test(str)) {
      return "Start and end with a letter or number; use only letters, numbers, hyphens or periods.";
    }
    if (isHost && os === "windows" && (str.includes(".") || /^\d+$/.test(str))) {
      return "Windows host names cannot contain periods or consist only of numbers.";
    }
  }
  if (matches(field, ["username", "adminusername", "administratorusername", "masterusername"])) {
    const max = os === "windows" ? 20 : 32;
    if (str.length > max) return `Username must be 1-${max} characters.`;
    if (blockedUsers.has(str.toLowerCase())) return "This username is reserved by Azure. Choose another username.";
    if (os === "linux" && !/^[a-z_][a-z0-9_-]*$/i.test(str)) {
      return "Linux usernames must start with a letter or underscore and contain only letters, numbers, underscores or hyphens.";
    }
    if (/[\\/"[\]:;|=,+*?<>@\s]/.test(str) || str.endsWith(".")) {
      return "Username contains invalid characters or ends with a period.";
    }
  }
  if (matches(field, ["password", "adminpassword", "administratorpassword", "masterpassword"])) {
    const max = os === "linux" ? 72 : 123;
    if (str.length < 12 || str.length > max) return `Password must be 12-${max} characters.`;
    if (Array.from(str).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) {
      return "Password cannot contain control characters.";
    }
    if ([/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((rule) => rule.test(str)).length < 3) {
      return "Use at least three of: uppercase letters, lowercase letters, numbers and symbols.";
    }
    if (blockedPasswords.has(str)) return "This password is not permitted by Azure.";
  }
  if (field.matchWith || matches(field, ["confirmpassword", "confirmadminpassword", "confirmadministratorpassword", "confirmmasterpassword"])) {
    const passwordField = field.matchWith
      ? fields.find((candidate) => candidate.fieldName === field.matchWith)
      : find(fields, ["password", "adminpassword", "administratorpassword", "masterpassword"]);
    if (!passwordField || value !== read(passwordField, data)) return "Passwords do not match.";
  }
  if (matches(field, ["sshpublickey", "sshkey", "publickey", "sshpublickeysourcevalue"])) {
    if (!isValidSshKey(str)) return "Enter an OpenSSH RSA key of at least 2048 bits or an Ed25519 public key.";
  }
  if (matches(field, ["privateip", "privateipaddress", "publicipaddress"])) {
    if (!isIpv4(str)) return "Enter a valid IPv4 address.";
    if (matches(field, ["privateip", "privateipaddress"])) {
      const subnetField = find(fields, ["subnetprefix", "subnetaddressprefix", "subnetaddressrange"]);
      const subnet = subnetField && parseCidr(read(subnetField, data));
      if (subnet) {
        const offset = ipv4Number(str) - subnet.address;
        if (offset < 0 || offset >= subnet.size) return "Private IP address must be inside the subnet range.";
        if (offset < 4 || offset === subnet.size - 1) return "Azure reserves the first four and last IP addresses in each subnet.";
      }
    }
  }
  if (matches(field, ["addressprefix", "addressspace", "subnetprefix", "subnetaddressprefix", "subnetaddressrange", "virtualnetworkaddressspace"])) {
    const parts = str.split("/");
    if (parts.length !== 2 || !isIpv4(parts[0]) || !/^\d{1,2}$/.test(parts[1]) || Number(parts[1]) > 32) {
      return "Enter a valid IPv4 CIDR range, for example 10.0.0.0/24.";
    }
    if (!parseCidr(str)) return "CIDR range must start at its network address.";
    if (Number(parts[1]) < 2 || Number(parts[1]) > 29) return "Azure IPv4 network and subnet prefixes must be between /2 and /29.";
  }
  if (matches(field, ["port", "sshport", "rdpport", "destinationport"])) {
    if (!/^\d+$/.test(str) || Number(str) < 1 || Number(str) > 65535) return "Port must be an integer from 1 to 65535.";
  }
  if (matches(field, ["osdisksize", "osdisksizegb", "osdisksizegib", "datadisksize", "datadisksizegb", "datadisksizegib", "disksize", "disksizegb", "disksizegib"])) {
    const isOsDisk = matches(field, ["osdisksize", "osdisksizegb", "osdisksizegib"]);
    const max = isOsDisk ? 4095 : 32767;
    if (!/^\d+$/.test(str) || Number(str) < 1 || Number(str) > max) return `Disk size must be a whole number from 1 to ${max} GiB.`;
  }
  if (matches(field, ["lun", "logicalunitnumber"])) {
    if (!/^\d+$/.test(str) || Number(str) > 63) return "LUN must be an integer from 0 to 63.";
  }
  return "";
};

export const validateAzureVmFields = (fields, data, isVisible, allFields = fields) => {
  const errors = [];
  fields.forEach((field) => {
    if (!isVisible(field, data)) return;
    const message = getAzureVmFieldError(field, data, allFields);
    if (message) errors.push({ field, message });
    if (field.fieldType === "collection") {
      const rows = read(field, data);
      if (Array.isArray(rows)) {
        const usedLuns = new Set();
        rows.forEach((row, index) => {
          const lunField = find(field.collectionFields || [], ["lun", "logicalunitnumber"]);
          if (lunField && isVisible(lunField, row)) {
            const lun = read(lunField, row);
            if (text(lun).trim() !== "") {
              const normalizedLun = Number(lun);
              if (usedLuns.has(normalizedLun)) {
                errors.push({ field, message: `${field.fieldLabel || field.fieldName} ${index + 1}: Each data disk must have a unique LUN.` });
              }
              usedLuns.add(normalizedLun);
            }
          }
          validateAzureVmFields(field.collectionFields || [], row, isVisible, [
            ...allFields, ...(field.collectionFields || []),
          ]).forEach((error) => errors.push({
            field,
            message: `${field.fieldLabel || field.fieldName} ${index + 1}: ${error.message}`,
          }));
        });
      }
    }
  });
  return errors;
};
